import IconLock from '@apollo/icons/default/IconLock.svg';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import {
  Button,
  ButtonGroup,
  Divider,
  Group,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import { groupBy, sortBy } from 'lodash';
import React, { useState } from 'react';

import {
  ClientName,
  ClientVersionList,
} from 'src/components/clientVersionList/ClientVersionList';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';

import { ExcludedClient } from '../transforms';

interface Props {
  graphId: string;
  excludedClients: ExcludedClient[] | null;
  appendedExcludedClients?: ExcludedClient[];
  onExcludedClientRemove?: (clientName: string) => void;
  canViewVersions?: boolean;
  showActions?: boolean;
}

export const ExcludedClientsTable = ({
  graphId,
  excludedClients,
  appendedExcludedClients,
  onExcludedClientRemove,
  canViewVersions,
  showActions,
}: Props) => {
  const [removingClientName, setRemovingClientName] = useState<string | null>(
    null,
  );

  const groupByVersion = (clients: ExcludedClient[], isAppended: boolean) => {
    const clientsGroupedByName = groupBy(clients, (client) => client.name);
    return Object.entries(clientsGroupedByName).map(([name, data]) => ({
      name,
      versions: data.every((client) => client.version === null)
        ? null
        : data
            .filter((client) => client.version !== null)
            .map((client) => client.version as string),
      isAppended,
    }));
  };

  const allClients = sortBy(
    [
      ...groupByVersion(excludedClients ?? [], false),
      ...groupByVersion(appendedExcludedClients ?? [], true),
    ],
    (client) => client.name,
  );

  return (
    <>
      {allClients.length > 0 ? (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th className="w-1/2">Client</Th>
                {canViewVersions && <Th className="w-1/2">Client version</Th>}
                <Th isActionsMenu className="w-40">
                  <span className="sr-only">Remove</span>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {allClients.map((client) => {
                const { name, versions, isAppended } = client;

                return (
                  <Tr key={`${name}_${isAppended}`}>
                    <Td>
                      <Group gap={2}>
                        <ClientName>{name}</ClientName>
                        {isAppended && (
                          <IconLock className="text-icon-primary" />
                        )}
                      </Group>
                    </Td>
                    {canViewVersions && (
                      <Td>
                        {versions === null ? (
                          <span className="italic">All versions</span>
                        ) : (
                          <ClientVersionList
                            clientName={name}
                            versions={versions}
                          />
                        )}
                      </Td>
                    )}
                    <Td isActionsMenu>
                      {isAppended || !showActions ? (
                        <div className="size-8" />
                      ) : (
                        <PermissionGuard
                          graphId={graphId}
                          graphPermissions="canWriteCheckConfiguration"
                          fallback={null}
                        >
                          <IconButton
                            aria-label="Remove client name"
                            variant="secondary"
                            onClick={() => setRemovingClientName(name)}
                          >
                            <IconTrash />
                          </IconButton>
                        </PermissionGuard>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <>
          <Divider />
          <div className="text-center text-base text-placeholder">
            no clients excluded
          </div>
        </>
      )}
      {onExcludedClientRemove && removingClientName !== null && (
        <Modal isOpen size="lg" onClose={() => setRemovingClientName(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Remove client exclusion?</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p>
                {removingClientName === '' ? (
                  <em>Unidentified clients</em>
                ) : (
                  <span>
                    Clients named <em>{removingClientName}</em>
                  </span>
                )}{' '}
                will no longer be excluded from checks.
              </p>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setRemovingClientName(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  type="button"
                  onClick={() => {
                    onExcludedClientRemove(removingClientName);
                    setRemovingClientName(null);
                  }}
                >
                  Yes, remove
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
